import { useState, useEffect, useContext, useMemo, useRef } from "react";
import axios from "axios";
import LocContext from "../context/locContext";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import { useLocation } from "react-router-dom";

export default function ClientViewDetail() {
  const { userData } = useContext(LocContext);
  const id = userData?.id;

  const location = useLocation();
  const category = location.state?.category || "all";

  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState({ key: "name", dir: "asc" });
  const [categoriesList, setCategoriesList] = useState(["All"]);
  // const [selectedCategories, setSelectedCategories] = useState(["All"]);
  const [selectedCategories, setSelectedCategories] = useState([]);


  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Scroll refs
  const tableScrollRef = useRef(null);
  // const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);

  // category dropdown refs/state
  const [catOpen, setCatOpen] = useState(false);
  const [catSearch, setCatSearch] = useState("");
  const catRef = useRef(null);

  useEffect(() => {
    const getFundsData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/mutualfund/category/${category}`,
        );

        if (res.status === 200 && Array.isArray(res.data.data)) {
          const fundsData = res.data.data;
          setFunds(fundsData);

          const uniqueSubCategories = [
            "All",
            ...new Set(
              fundsData.map((item) => item.sub_category).filter(Boolean),
            ),
          ];
          setCategoriesList(uniqueSubCategories);
        } else {
          setFunds([]);
          setCategoriesList(["All"]);
        }
      } catch (err) {
        console.error(err);
        setFunds([]);
        setCategoriesList(["All"]);
      } finally {
        setLoading(false);
      }
    };

    getFundsData();
  }, [id, category]);

  function sortToggle(key) {
    setSortBy((s) => {
      if (s.key === key) return { key, dir: s.dir === "asc" ? "desc" : "asc" };
      return { key, dir: "asc" };
    });
  }

  const columns = [
    { key: "name", label: "Scheme", width: 360 },
    { key: "sub_category", label: "Sub Category", width: 360 },
    { key: "nav", label: "NAV", width: 90 },
    { key: "aum", label: "AUM (cr)", width: 110 },
    { key: "expense_ratio", label: "Expense Ratio", width: 120 },
    { key: "m6", label: "6 Month", width: 100 },
    { key: "y1", label: "1 Year", width: 100 },
    { key: "y2", label: "2 Year", width: 100 },
    { key: "y3", label: "3 Year", width: 100 },
    { key: "y5", label: "5 Year", width: 100 },
    { key: "y7", label: "7 Year", width: 100 },
    { key: "y10", label: "10 Year", width: 100 },
    { key: "y15", label: "15 Year", width: 100 },
    { key: "y20", label: "20 Year", width: 100 },
    { key: "y25", label: "25 Year", width: 100 },
    { key: "since_inception", label: "Since Inception", width: 140 },
    { key: "launch_date", label: "Launch Date", width: 120 },
    { key: "rating", label: "Rating", width: 110 },
  ];

  // normalize fields
  const normalized = useMemo(() => {
    return funds.map((f) => {
      const get = (paths) =>
        paths.reduce((acc, p) => {
          if (acc !== undefined && acc !== null && acc !== "") return acc;
          return f[p];
        }, undefined);

      return {
        ...f,
        nav: get(["nav", "NAV", "net_asset_value"]) ?? "-",
        aum: get(["aum", "AUM", "fund_size", "fundSize"]) ?? "-",
        expense_ratio: get(["expense_ratio", "expenseRatio", "expense"]) ?? "-",
        m6: get(["m6", "6m", "return_6m", "return6m", "six_month"]) ?? "-",
        y1: get(["y1", "1y", "return_1y", "return1y", "one_year"]) ?? "-",
        y2: get(["y2", "2y", "return_2y", "return2y"]) ?? "-",
        y3: get(["y3", "3y", "return_3y", "three_year"]) ?? "-",
        y5: get(["y5", "5y", "return_5y", "five_year"]) ?? "-",
        y7: get(["y7", "7y", "return_7y", "seven_year"]) ?? "-",
        y10: get(["y10", "10y", "return_10y", "ten_year"]) ?? "-",
        y15: get(["y15", "15y", "return_15y"]) ?? "-",
        y20: get(["y20", "20y", "return_20y"]) ?? "-",
        y25: get(["y25", "25y", "return_25y"]) ?? "-",
        since_inception:
          get(["since_inception", "sinceInception", "since_incep"]) ?? "-",
        launch_date: get(["launch_date", "launchDate", "launched_on"]) ?? "-",
        rating: get(["rating", "fund_rating", "star_rating"]) ?? 0,
      };
    });
  }, [funds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = normalized;

    if (q) {
      arr = arr.filter(
        (r) =>
          (r.schemes && r.schemes.toString().toLowerCase().includes(q)) ||
          (r.sub_category &&
            r.sub_category.toString().toLowerCase().includes(q)),
      );
    }

    const realSelected = selectedCategories.filter((c) => c !== "All");
    if (realSelected.length > 0) {
      arr = arr.filter((item) => realSelected.includes(item.sub_category));
    }

    if (sortBy?.key) {
      arr = [...arr].sort((a, b) => {
        const aVal = (a[sortBy.key] ?? "").toString();
        const bVal = (b[sortBy.key] ?? "").toString();

        const an = parseFloat(aVal.toString().replace(/[^0-9.-]+/g, ""));
        const bn = parseFloat(bVal.toString().replace(/[^0-9.-]+/g, ""));
        if (!isNaN(an) && !isNaN(bn)) {
          return sortBy.dir === "asc" ? an - bn : bn - an;
        }
        if (aVal < bVal) return sortBy.dir === "asc" ? -1 : 1;
        if (aVal > bVal) return sortBy.dir === "asc" ? 1 : -1;
        return 0;
      });
    }

    return arr;
  }, [normalized, search, sortBy, selectedCategories]);

  // Pagination
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // Sync top & bottom with table scroll
useEffect(() => {
  const table = tableScrollRef.current;
  const bottom = bottomScrollRef.current;

  if (!table || !bottom) return;

  const syncing = { active: false };

  const beginSync = () => {
    syncing.active = true;
    clearTimeout(beginSync._t);
    beginSync._t = setTimeout(() => (syncing.active = false), 40);
  };

  const onTableScroll = () => {
    if (syncing.active) return;
    beginSync();
    bottom.scrollLeft = table.scrollLeft;
  };

  const onBottomScroll = () => {
    if (syncing.active) return;
    beginSync();
    table.scrollLeft = bottom.scrollLeft;
  };

  table.addEventListener("scroll", onTableScroll, { passive: true });
  bottom.addEventListener("scroll", onBottomScroll, { passive: true });

  const adjustWidth = () => {
    bottom.querySelector(".bottom-inner").style.width =
      table.scrollWidth + "px";
  };

  adjustWidth();
  const ro = new ResizeObserver(adjustWidth);
  ro.observe(table);
  window.addEventListener("resize", adjustWidth);

  return () => {
    table.removeEventListener("scroll", onTableScroll);
    bottom.removeEventListener("scroll", onBottomScroll);
    ro.disconnect();
    window.removeEventListener("resize", adjustWidth);
  };
}, [pagedData, filtered, normalized]);


  const investLink =
    userData?.mutual_status === 1
      ? "https://gpsfinvest.investwell.app/app/#/login"
      : "/Invest-application";

  const renderPageButtons = () => {
    const pages = [];
    const maxButtons = 3;
    let start = Math.max(1, page - Math.floor(maxButtons / 2));
    let end = start + maxButtons - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`pg-btn ${i === page ? "active" : ""}`}
          onClick={() => setPage(i)}
          aria-label={`Page ${i}`}
        >
          {i}
        </button>,
      );
    }
    return pages;
  };

  // close category on outside click / ESC
  useEffect(() => {
    function onDoc(e) {
      if (catRef.current && !catRef.current.contains(e.target))
        setCatOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setCatOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const toggleCategory = (c) => {
    if (c === "All") {
      const allSelected = selectedCategories.length === categoriesList.length;
      if (!allSelected) setSelectedCategories([...categoriesList]);
      else setSelectedCategories([]);
      return;
    }

    setSelectedCategories((prev) => {
      let updated = [...prev];
      if (updated.includes("All"))
        updated = categoriesList.filter((x) => x !== "All");

      if (updated.includes(c)) updated = updated.filter((x) => x !== c);
      else updated.push(c);

      const realCats = categoriesList.filter((x) => x !== "All");
      const allSelected = realCats.every((x) => updated.includes(x));
      return allSelected ? [...categoriesList] : updated;
    });
  };

  return (
    <>
      <style>{`
        /* container */
        .viewdetail-wrap { padding-bottom: 60px; }
        .table-shell { background: #fff; border-radius: 10px; padding: 12px; box-shadow: 0 12px 30px rgba(3,186,187,0.06); }

        /* top controls (search + pagination) */
        .table-top { display:flex; gap:12px; align-items:center; justify-content:space-between; margin-bottom:8px; flex-wrap:wrap; }
        .controls { display:flex; gap:12px; align-items:center; flex-wrap:wrap; margin: 22px 0 0 0;}

        .controls input[type="search"]{ padding:10px 14px; border-radius:8px; border:1px solid #e8f0f0; min-width:260px; background:#fbffff; }
        .page-info { color:#6b7280; font-size:.9rem; }

        /* pagination styles */
        .pagination { display:flex; gap:8px; align-items:center; }
        .pagination .pg-btn, .pagination .pg-arrow {
          color: black;
          width:34px; height:34px; border-radius:8px; border:1px solid #e6eef0; background:#fff; display:inline-flex; align-items:center; justify-content:center; cursor:pointer;
        }
        .pagination .pg-btn.active { background:#0bc1ab ; color:#fff; border-color:#0bc1ab ; }
        .pagination .pg-arrow.disabled { opacity:0.45; cursor:not-allowed; }

        .page-size { padding:8px 10px; border-radius:8px; border:1px solid #e6eef0; background:#fff; }

        /* TOP mirrored scrollbar (visible) */
        .table-scroll-top { overflow-x:auto; overflow-y:hidden; height: 18px; margin-bottom:10px; border-radius:8px; background: transparent; }
        .table-scroll-top .top-inner { height:1px; } /* width set by JS */

        /* MAIN table scroll (keep native scrollbar visible) */
.table-scroll {
  overflow-x: scroll;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* hide native scrollbar */
}

        /* table */
        table.fund-table { border-collapse: collapse; width: 100%; min-width: 2200px; }
        table.fund-table thead th {
          position: sticky; top:0; background: linear-gradient(180deg,#ffffff,#f7ffff); z-index:2;
          text-align:left; padding:10px 12px; border-bottom:1px solid rgba(0,0,0,0.06);
          color:#0bc1ab ; font-weight:700; font-size:.88rem; white-space:nowrap;
        }
        table.fund-table tbody td { padding:10px 12px; border-bottom:1px solid rgba(11,11,11,0.03); font-size:.9rem; color:#233243; }
        table.fund-table tbody tr:hover { background: rgba(15,118,110,0.03); }

        .sort-indicator { display:inline-block; margin-left:6px; opacity:.6; font-size:.78rem; }

        .fav-btn { background:transparent; border:none; cursor:pointer; padding:4px 6px; }
        .heart { width:18px; height:18px; display:inline-block; vertical-align:middle; }

        .stars { display:flex; gap:4px; align-items:center; }
        .star { width:14px; height:14px; display:inline-block; }

        /* BOTTOM mirrored scrollbar (visible) */
        .table-scroll-bottom {
          overflow-x: auto;
          overflow-y: hidden;
          height: 18px;
          margin-top: 10px;
          border-radius: 8px;
          background: transparent;
        }
        .table-scroll-bottom .bottom-inner { height: 1px; } /* width set by JS */

        /* bottom summary */
        .bottom-row { margin-top:12px; display:flex; justify-content:space-between; align-items:center; }

        /* responsive tweaks */
        @media (max-width: 767.98px) {
          .controls input[type="search"]{ min-width:140px; }
          table.fund-table { min-width:1500px; }
        }

        .cat-field-box {
          display: inline-block;
          padding: 10px 12px;
          border-radius: 6px;
          background: #ffffff;
          border: 2px solid #9de3dd;
          cursor: pointer;
          width: 100%;
          text-align: left;
          font-weight: 600;
          color: #0bc1ab ;
        }

        /* dropdown menu and category list styles (unchanged) */
        .cat-menu {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          max-height: 300px;
          background: #fff;
          border: 1px solid rgba(3,186,187,0.12);
          border-radius: 8px;
          box-shadow: 0 10px 30px rgba(3,186,187,0.06);
          z-index: 2200;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .cat-search {
          width: 100%;
          padding: 8px 10px;
          border-radius: 6px;
          border: 1px solid #eef6f5;
          outline: none;
        }
        .cat-list { overflow-y: auto; padding: 6px 4px; max-height: 260px; }
        .cat-item { padding: 8px 10px; border-radius: 6px; display: flex; align-items: center; gap: 8px; margin: 4px; }
        .cat-item:hover { background: rgba(3,186,187,0.04); }
        .chk-box { width: 22px; height: 22px; border: 2px solid #0bc1ab; border-radius: 5px; display: inline-flex; align-items: center; justify-content: center; background: #fff; }
        .chk-box.chk-on { background: #0bc1ab; border-color: #0bc1ab; }
        .chk-tick { color: #fff; font-weight: 700; font-size: 13px; line-height: 1; }

      `}</style>

     
      <ClientNavbar />
      <div className="page-content viewdetail-wrap" style={{ background: "#f1f5f9" }}>
         <div className="card shadow-sm p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h3 className="text-primary mb-0">Mutual Fund Details</h3>
            </div>
            <nav
              aria-label="breadcrumb"
              style={{ "--bs-breadcrumb-divider": "none" }}
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/overview">Dashboard</NavLink> /
                </li>
                     <li className="breadcrumb-item" aria-current="page">
                                    <NavLink to="/clientmutualfunds">Available Mutual Funds</NavLink> /
                                  </li> 
                <li className="breadcrumb-item active" aria-current="page">
                  Mutual Fund Details
                </li>
              </ol>
            </nav>
            </div>
 <div className="card shadow-sm p-4 mb-4">
            <div className="table-shell">
              {/* TOP controls */}
              <div className="table-top">
                <div
                  className="controls mt-2"
                  style={{ alignItems: "flex-start", gap: 16 }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      minWidth: 220,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "18px",
                        color: "#0bc1ab ",
                        lineHeight: 1,
                      }}
                    >
                      Sub Category :
                    </div>

                    <div
                      style={{ position: "relative", minWidth: 220 }}
                      ref={catRef}
                    >
                      <button
                        type="button"
                        className="cat-field-box"
                        onClick={() => {
                          setCatOpen((s) => !s);
                          setCatSearch("");
                        }}
                        aria-haspopup="listbox"
                        aria-expanded={catOpen}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              color: "#0f766e",
                              fontWeight: 600,
                              fontSize: ".95rem",
                            }}
                          >
                            {selectedCategories.length === 0 ||
                            (selectedCategories.length === 1 &&
                              selectedCategories[0] === "All")
                              ? "Select Sub Category"
                              : selectedCategories.length > 2
                              ? `${selectedCategories
                                  .slice(0, 2)
                                  .join(", ")} +${
                                  selectedCategories.length - 2
                                }`
                              : selectedCategories.join(", ")}
                          </div>
                          <div style={{ opacity: 0.8, marginLeft: 8 }}>▾</div>
                        </div>
                      </button>

                      {catOpen && (
                        <div className="cat-menu">
                          <div style={{ padding: "8px 10px" }}>
                            <input
                              autoFocus
                              type="search"
                              value={catSearch}
                              onChange={(e) => setCatSearch(e.target.value)}
                              placeholder="Type to Search..."
                              className="cat-search"
                              aria-label="Search categories"
                            />
                          </div>

                          <div
                            className="cat-list"
                            role="listbox"
                            tabIndex={-1}
                          >
                            {categoriesList
                              .filter((c) =>
                                c
                                  .toLowerCase()
                                  .includes(catSearch.trim().toLowerCase()),
                              )
                              .map((c) => (
                                <label
                                  key={c}
                                  className={`cat-item ${
                                    selectedCategories.includes(c)
                                      ? "selected"
                                      : ""
                                  }`}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    cursor: "pointer",
                                  }}
                                >
                                  <div
                                    onClick={() => toggleCategory(c)}
                                    className={`chk-box ${
                                      selectedCategories.includes(c)
                                        ? "chk-on"
                                        : ""
                                    }`}
                                  >
                                    {selectedCategories.includes(c) && (
                                      <span className="chk-tick">✔</span>
                                    )}
                                  </div>

                                  <span
                                    style={{
                                      flex: 1,
                                      fontWeight: 600,
                                      color: "#0b6f68",
                                    }}
                                  >
                                    {c}
                                  </span>
                                  {/* <span style={{ opacity: 0.45 }}>›</span> */}
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <input
                    type="search"
                    placeholder="Search scheme or category..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    style={{
                      minWidth: 300,
                      padding: "10px 14px",
                      borderRadius: 8,
                      marginTop: "23px",
                      border: "1px solid #e6eef0",
                    }}
                  />

                  <div className="page-info " style={{ paddingTop: "35px" }}>
                    {filtered.length} results
                  </div>
                </div>
              </div>

              {/* TOP mirrored scrollbar (visible) */}
              {/* <div className="table-scroll-top" ref={topScrollRef}>
                <div className="top-inner" />
              </div> */}

              {/* MAIN table scroll (native scrollbar visible) */}
              <div className="table-scroll" ref={tableScrollRef}>
                <table className="fund-table">
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          style={{
                            width: col.width ? `${col.width}px` : "auto",
                            cursor: "pointer",
                          }}
                          onClick={() => sortToggle(col.key)}
                          title={`Sort by ${col.label}`}
                        >
                          {col.label}
                          {sortBy.key === col.key && (
                            <span className="sort-indicator">
                              {sortBy.dir === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={columns.length} style={{ padding: 24 }}>
                          Loading...
                        </td>
                      </tr>
                    ) : pagedData.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} style={{ padding: 24 }}>
                          No data found
                        </td>
                      </tr>
                    ) : (
                      pagedData.map((f) => (
                        <tr key={f.id ?? f.name}>
                          <td style={{ width: 360 }}>
                            <div style={{ fontWeight: 700, color: "#0bc1ab " }}>
                              {f.name}
                            </div>
                            <div
                              style={{ fontSize: ".85rem", color: "#6b7280" }}
                            >
                              {f.schemes ?? ""}
                            </div>
                          </td>
                          <td style={{ width: 90 }}>{f.sub_category ?? "-"}</td>
                          <td style={{ width: 90 }}>{f.current_nav ?? "-"}</td>
                          <td style={{ width: 110 }}>{f.aum_cr ?? "-"}</td>
                          <td style={{ width: 120 }}>
                            {f.expense_ratio ?? "-"}
                          </td>
                          <td style={{ width: 100 }}>{f.m6 ?? "-"}</td>
                          <td style={{ width: 100 }}>{f.y1 ?? "-"}</td>
                          <td style={{ width: 100 }}>{f.y2 ?? "-"}</td>
                          <td style={{ width: 100 }}>{f.y3 ?? "-"}</td>
                          <td style={{ width: 100 }}>{f.y5 ?? "-"}</td>
                          <td style={{ width: 100 }}>{f.y7 ?? "-"}</td>
                          <td style={{ width: 100 }}>{f.y10 ?? "-"}</td>
                          <td style={{ width: 100 }}>{f.y15 ?? "-"}</td>
                          <td style={{ width: 100 }}>{f.y20 ?? "-"}</td>
                          <td style={{ width: 100 }}>{f.y25 ?? "-"}</td>
                          <td style={{ width: 140 }}>
                            {f.since_inception ?? "-"}
                          </td>
                          <td style={{ width: 120 }}>{f.launch_date ?? "-"}</td>
                          <td style={{ width: 110 }}>
                            <div
                              className="stars"
                              title={`Rating ${f.rating || 0}`}
                            >
                              {Array.from({ length: 5 }).map((_, i) => {
                                const filled = i < Math.round(f.rating || 0);
                                return (
                                  <svg
                                    key={i}
                                    className="star"
                                    viewBox="0 0 24 24"
                                    fill={filled ? "#FFB020" : "none"}
                                    stroke="#FFB020"
                                  >
                                    <path
                                      strokeWidth="1.2"
                                      d="M12 .587l3.09 6.26L22 8.27l-5 4.87L18.18 22 12 18.77 5.82 22 7 13.14 2 8.27l6.91-1.42z"
                                    />
                                  </svg>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* BOTTOM mirrored scrollbar (visible) */}
              {/* ONE bottom scrollbar */}
              <div className="table-scroll-bottom" ref={bottomScrollRef}>
                <div className="bottom-inner" />
              </div>

              {/* bottom summary / page controls */}
              <div className="bottom-row">
                <div className="page-info">
                  Showing {(page - 1) * pageSize + 1} -{" "}
                  {Math.min(page * pageSize, total)} of {total}
                </div>
                <div className="controls">
                  <div
                    className="pagination"
                    role="navigation"
                    aria-label="Pagination"
                  >
                    <button
                      className={`pg-arrow ${page === 1 ? "disabled" : ""}`}
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                      title="First"
                    >
                      «
                    </button>

                    <button
                      className={`pg-arrow ${page === 1 ? "disabled" : ""}`}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      title="Prev"
                    >
                      ‹
                    </button>

                    {renderPageButtons()}

                    <button
                      className={`pg-arrow ${
                        page === totalPages ? "disabled" : ""
                      }`}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                      title="Next"
                    >
                      ›
                    </button>

                    <button
                      className={`pg-arrow ${
                        page === totalPages ? "disabled" : ""
                      }`}
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                      title="Last"
                    >
                      »
                    </button>
                  </div>

                  <select
                    className="page-size"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    <option value={10}>10 / page</option>
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
              </div>
            </div>
            </div>
          </div>
       </>
  );
}
